import { useState, useEffect, useCallback } from "react";
import { Loader2, ExternalLink, Clock, Calendar, Users, AlertTriangle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return iso; }
}

function formatPrice(price) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(price);
}

export default function AgencyTracker() {
  const [listings, setListings] = useState([]);
  const [stats, setStats] = useState({ total: 0, non_exclusive: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("non_exclusive"); // Default to non-exclusive

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "non_exclusive"
        ? { status: "non_exclusive", limit: 100 }
        : { limit: 100 };
      const [listingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/agency-listings`, { params }),
        axios.get(`${API}/agency-listings/stats`),
      ]);
      setListings(listingsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      console.error("Error fetching agency listings:", e);
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div data-testid="agency-tracker-page" className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-4xl font-black text-zinc-900 tracking-tighter">
          Monitoraggio Agenzie
        </h1>
        <p className="text-xs sm:text-sm text-zinc-500 mt-0.5">
          Annunci proposti da più agenzie (non in esclusiva) — opportunità di contatto diretto col proprietario
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="border border-zinc-200 rounded-sm p-4">
          <div className="text-xs tracking-wider uppercase font-semibold text-zinc-400">Totale tracciati</div>
          <div className="text-3xl font-black text-zinc-900 mt-1">{stats.total}</div>
        </div>
        <div className="border border-amber-200 bg-amber-50/50 rounded-sm p-4">
          <div className="text-xs tracking-wider uppercase font-semibold text-amber-500 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Non in esclusiva
          </div>
          <div className="text-3xl font-black text-amber-600 mt-1">{stats.non_exclusive || 0}</div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-1 p-1 bg-zinc-100 rounded-sm w-fit">
        <button
          data-testid="filter-non-exclusive"
          onClick={() => setFilter("non_exclusive")}
          className={`px-3 py-1.5 rounded-sm text-xs font-semibold transition-all ${
            filter === "non_exclusive" ? "bg-white text-amber-600 shadow-sm" : "text-zinc-500 hover:text-zinc-700"
          }`}
        >
          <AlertTriangle className="w-3 h-3 inline mr-1" />
          Non esclusivi ({stats.non_exclusive || 0})
        </button>
        <button
          data-testid="filter-all"
          onClick={() => setFilter("all")}
          className={`px-3 py-1.5 rounded-sm text-xs font-semibold transition-all ${
            filter === "all" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-700"
          }`}
        >
          Tutti ({stats.total})
        </button>
      </div>

      {/* Listings */}
      <div className="space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20 text-zinc-400 text-sm">
            {filter === "non_exclusive"
              ? "Nessun annuncio proposto da più agenzie trovato."
              : "Nessun annuncio di agenzia tracciato."}
          </div>
        ) : (
          listings.map(listing => (
            <div
              key={listing.id}
              data-testid={`agency-listing-${listing.id}`}
              className={`border rounded-sm p-3 sm:p-4 transition-all ${
                listing.non_exclusive
                  ? "border-amber-200 bg-amber-50/30"
                  : "border-zinc-200 hover:shadow-sm"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                    {listing.non_exclusive && (
                      <span className="text-[9px] font-bold text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-sm border border-amber-200">
                        <Users className="w-2.5 h-2.5 inline mr-0.5" />
                        {listing.agencies_count || listing.agencies?.length || 2} agenzie — Non esclusivo
                      </span>
                    )}
                    {listing.property_category === "commerciale" && listing.property_type && (
                      <span className="text-[9px] font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-sm border border-indigo-200">
                        {listing.property_type}
                      </span>
                    )}
                  </div>
                  <h3 className="text-sm font-semibold text-zinc-900 truncate">
                    {listing.title}
                  </h3>
                  <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                    <span>{listing.zone}</span>
                    {listing.sqm > 0 && <span>{listing.sqm} m²</span>}
                    {listing.rooms > 0 && <span>{listing.rooms} locali</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 text-[10px] text-zinc-400">
                    {listing.published_at && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Pubblicato: {formatDate(listing.published_at)}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Rilevato: {formatDate(listing.first_seen_at)}
                    </span>
                  </div>
                  {/* Agency names — clickable links */}
                  {listing.agencies && listing.agencies.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {listing.agencies.map((agency, idx) => {
                        const agencyUrl = listing.agencies_links?.[agency];
                        return agencyUrl ? (
                          <a key={idx} href={agencyUrl} target="_blank" rel="noopener noreferrer"
                            className="text-[10px] font-semibold px-2 py-0.5 rounded-sm bg-zinc-100 text-blue-600 border border-zinc-200 hover:bg-blue-50 hover:border-blue-300 transition-colors">
                            {agency} <ExternalLink className="w-2.5 h-2.5 inline" />
                          </a>
                        ) : (
                          <span key={idx} className="text-[10px] font-semibold px-2 py-0.5 rounded-sm bg-zinc-100 text-zinc-600 border border-zinc-200">
                            {agency}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-lg font-black text-zinc-900">
                    {formatPrice(listing.price)}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
